import React, { useMemo } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import {
    ConnectionProvider,
    IntegrationConnection,
} from "../../../common/types/integrationConnection";
import { INTEGRATION_CONNECTIONS_SETTINGS } from "../../../common/helpers/integrationConnection";
import { Collapsible } from "../../general/Collapsible/Collapsible";
import { DisplayIntegrationAccount } from "../../integrationAccount/DisplayIntegrationAccount";
import { IntegrationConnectionProviderIcon } from "../../integrationConnection/IntegrationConnetionProviderIcon";
import { timeAgo } from "../../../helpers/date";
import { AlertIcon, ClockIcon } from "../../../icons";
import { NewIntegrationConnection } from "../../integrationConnection/NewIntegrationConnection";
import css from "./ProviderIntegrationConnections.module.scss";

interface Props {
    integrationConnections: IntegrationConnection[];
    provider: ConnectionProvider;
}

export const ProviderIntegrationConnections: React.FC<Props> = ({
    integrationConnections,
    provider,
}) => {
    const settings = INTEGRATION_CONNECTIONS_SETTINGS[provider];

    const mostRecentBalanceSyncedAt = useMemo(() => {
        if (!integrationConnections?.length) {
            return null;
        }

        const dates = integrationConnections
            .map((conn) => conn.integrationAccounts?.[0]?.balanceSyncedAt)
            .filter((date): date is Date => date instanceof Date);

        if (!dates.length) {
            return null;
        }

        return dates.reduce(
            (latest, current) =>
                current.getTime() > latest.getTime() ? current : latest,
            dates[0],
        );
    }, [integrationConnections]);

    const hasConnectionError = integrationConnections.some(
        (connection) => connection.error,
    );

    if (settings.multipleAccountsPerConnection) {
        throw new Error("Not implemented yet");
    } else {
        return (
            <Collapsible
                className={css.container}
                header={
                    <div className={css.header}>
                        <IntegrationConnectionProviderIcon
                            provider={provider}
                        />
                        <main>
                            <h5>
                                {settings.label}
                                {hasConnectionError && (
                                    <OverlayTrigger
                                        overlay={
                                            <Tooltip
                                                id={`connection-error-${provider}`}
                                            >
                                                One or more accounts are
                                                disconnected.
                                            </Tooltip>
                                        }
                                    >
                                        <AlertIcon className="ml-2 icon-color-red-500" />
                                    </OverlayTrigger>
                                )}
                            </h5>
                            {mostRecentBalanceSyncedAt && (
                                <small>
                                    <ClockIcon
                                        height={12}
                                        width={12}
                                        className="icon-color-current"
                                    />
                                    {timeAgo(mostRecentBalanceSyncedAt)}
                                </small>
                            )}
                        </main>
                    </div>
                }
                footer={
                    <div className={css.footerSection}>
                        <NewIntegrationConnection
                            provider={provider}
                            btnVariant="secondary"
                        >
                            Add another account
                        </NewIntegrationConnection>
                    </div>
                }
            >
                <>
                    {integrationConnections?.map(
                        (connection) =>
                            connection.integrationAccounts?.[0] && (
                                <DisplayIntegrationAccount
                                    key={connection.id}
                                    className={css.displayAccount}
                                    connection={connection}
                                    integrationAccount={
                                        connection.integrationAccounts[0]
                                    }
                                />
                            ),
                    )}
                </>
            </Collapsible>
        );
    }
};
